import { Modal, Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import { IFood, MenuUpgrade } from "../models/IFood";
import { FoodCategory } from "../models/IFoodCategory";
import { getFoodCategories, postFood, translateToEnglish } from "../services/food";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  restaurantId: string;
}

const emptyExtra: MenuUpgrade = {
  id: "",
  menuUpgradeTitle: "",
  menuUpgradePrice: 0,
  menuUpgradeMaxQuantity: 0,
  menuUpgradeIsEnabled: true,
};

const emptyFood = (restaurantId: string): IFood => ({
  id: "",
  foodNameTr: "",
  foodNameEn: "",
  foodImagePath: "",
  foodContents: "",
  foodDescriptionTr: "",
  foodDescriptionEn: "",
  foodIsEnabled: true,
  foodPrice: 0,
  foodPriceText: "",
  menuUpgrade: [],
  restourantId: restaurantId,
  foodCategoryId: "",
  currencyId: "4263309b-97c8-49ff-94a3-cd2f39bb2c90",
  allergyIds: [],
});

function AddNewFoodPopup({ isOpen, onClose, onSaved, restaurantId }: Props) {
  const [formData, setFormData] = useState<IFood>(emptyFood(restaurantId));
  const [foodCategories, setFoodCategories] = useState<FoodCategory[]>([]);
  const [menuExtra, setMenuExtra] = useState<MenuUpgrade>(emptyExtra);
  const [activeTab, setActiveTab] = useState<"genel" | "ekstra">("genel");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [translating, setTranslating] = useState<{ nameEn?: boolean; descEn?: boolean }>({});

  const handleTranslate = async (field: "nameEn" | "descEn") => {
    const trText = field === "nameEn" ? formData.foodNameTr : formData.foodDescriptionTr;
    if (!trText?.trim()) return;
    setTranslating(prev => ({ ...prev, [field]: true }));
    try {
      const res = await translateToEnglish(trText.trim());
      if (field === "nameEn") set("foodNameEn", res.data);
      else set("foodDescriptionEn", res.data);
    } catch {}
    finally { setTranslating(prev => ({ ...prev, [field]: false })); }
  };

  // Popup açıldığında formu sıfırla
  useEffect(() => {
    if (isOpen) {
      setFormData(emptyFood(restaurantId));
      setMenuExtra(emptyExtra);
      setActiveTab("genel");
      setFile(null);
      setPreview(null);
      setSaving(false);
      setCountdown(0);
    }
  }, [isOpen, restaurantId]);

  useEffect(() => {
    getFoodCategories()
      .then(res => setFoodCategories(res.data ?? []))
      .catch(() => {});
  }, []);

  // 3 saniye geri sayım → kapat
  useEffect(() => {
    if (countdown <= 0) return;
    if (countdown === 3) {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setSaving(false);
            onSaved();
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [countdown]);

  const set = (field: keyof IFood, value: any) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = () => {
    if (saving) return;
    if (!formData.foodNameTr.trim()) {
      alert("Yemek adı zorunludur.");
      return;
    }
    if (!formData.foodCategoryId) {
      alert("Lütfen bir kategori seçin.");
      return;
    }
    setSaving(true);
    postFood(formData, file ?? undefined)
      .then(() => {
        setCountdown(3);
      })
      .catch(() => {
        setSaving(false);
        alert("Kaydetme başarısız oldu.");
      });
  };

  const addExtra = () => {
    if (!menuExtra.menuUpgradeTitle.trim()) return;
    setFormData(prev => ({
      ...prev,
      menuUpgrade: [...prev.menuUpgrade, { ...menuExtra }],
    }));
    setMenuExtra(emptyExtra);
  };

  const removeExtra = (index: number) => {
    setFormData(prev => ({
      ...prev,
      menuUpgrade: prev.menuUpgrade.filter((_, i) => i !== index),
    }));
  };

  const tabStyle = (tab: string): React.CSSProperties => ({
    padding: "8px 20px",
    border: "none",
    background: "none",
    borderBottom: activeTab === tab ? "3px solid #198754" : "3px solid transparent",
    fontWeight: activeTab === tab ? 700 : 400,
    color: activeTab === tab ? "#198754" : "#555",
    cursor: "pointer",
    transition: "all 0.2s",
  });

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered scrollable>
      {/* Başlık */}
      <Modal.Header closeButton style={{ background: "#f0fff4", borderBottom: "1px solid #c3e6cb" }}>
        <Modal.Title style={{ fontSize: "1.1rem" }}>
          <i className="bi bi-plus-circle-fill me-2 text-success"></i>
          <strong>Yeni Yemek Ekle</strong>
        </Modal.Title>
      </Modal.Header>

      {/* Kaydetme overlay */}
      {saving && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 9999,
          background: "rgba(255,255,255,0.92)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          borderRadius: "8px",
        }}>
          {countdown > 0 ? (
            <>
              <div style={{ fontSize: "3rem", color: "#198754" }}>
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <div className="fw-bold mt-2" style={{ fontSize: "1.1rem", color: "#198754" }}>
                Yemek başarıyla eklendi!
              </div>
              <div className="text-muted mt-1">{countdown} saniye içinde kapanıyor...</div>
              <div className="progress mt-3" style={{ width: "200px", height: "6px" }}>
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${(countdown / 3) * 100}%`, transition: "width 1s linear" }}
                />
              </div>
            </>
          ) : (
            <>
              <div className="spinner-border text-success mb-2" role="status" />
              <div className="text-muted">Kaydediliyor...</div>
            </>
          )}
        </div>
      )}

      {/* Sekmeler */}
      <div style={{ display: "flex", borderBottom: "1px solid #e0e0e0", padding: "0 16px" }}>
        <button style={tabStyle("genel")} onClick={() => setActiveTab("genel")}>
          <i className="bi bi-info-circle me-1"></i>Genel Bilgiler
        </button>
        <button style={tabStyle("ekstra")} onClick={() => setActiveTab("ekstra")}>
          <i className="bi bi-plus-circle me-1"></i>Ekstralar
          {formData.menuUpgrade.length > 0 && (
            <span className="badge bg-success ms-1" style={{ fontSize: "0.7rem" }}>
              {formData.menuUpgrade.length}
            </span>
          )}
        </button>
      </div>

      <Modal.Body style={{ padding: "20px 24px" }}>

        {/* ── GENEL BİLGİLER ── */}
        {activeTab === "genel" && (
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold small mb-1">
                Yemek Adı (TR) <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                placeholder="ör. Ton Balıklı Salata"
                value={formData.foodNameTr}
                maxLength={100}
                onChange={e => set("foodNameTr", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small mb-1 d-flex align-items-center justify-content-between">
                Yemek Adı (EN)
                {formData.foodNameTr?.trim() && (
                  <button type="button" className="translate-btn" onClick={() => handleTranslate("nameEn")} disabled={translating.nameEn}>
                    {translating.nameEn ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-translate"></i>}
                    Çevir
                  </button>
                )}
              </label>
              <input
                className="form-control"
                placeholder="ör. Tuna Salad"
                value={formData.foodNameEn}
                maxLength={100}
                onChange={e => set("foodNameEn", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small mb-1">Açıklama (TR)</label>
              <input
                className="form-control"
                value={formData.foodDescriptionTr}
                maxLength={160}
                onChange={e => set("foodDescriptionTr", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small mb-1 d-flex align-items-center justify-content-between">
                Açıklama (EN)
                {formData.foodDescriptionTr?.trim() && (
                  <button type="button" className="translate-btn" onClick={() => handleTranslate("descEn")} disabled={translating.descEn}>
                    {translating.descEn ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-translate"></i>}
                    Çevir
                  </button>
                )}
              </label>
              <input
                className="form-control"
                value={formData.foodDescriptionEn}
                maxLength={160}
                onChange={e => set("foodDescriptionEn", e.target.value)}
              />
            </div>
            <div className="col-md-12">
              <label className="form-label fw-semibold small mb-1">İçerik / Malzemeler</label>
              <input
                className="form-control"
                placeholder="ör. Mevsim yeşillikleri, ton balığı, mısır, domates"
                value={formData.foodContents}
                maxLength={160}
                onChange={e => set("foodContents", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small mb-1">
                Kategori <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={formData.foodCategoryId}
                onChange={e => set("foodCategoryId", e.target.value)}
              >
                <option value="" disabled>Kategori seçin</option>
                {foodCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.foodCategoryNameTr}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold small mb-1">Fiyat</label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control"
                  value={formData.foodPrice}
                  min={0}
                  onChange={e => set("foodPrice", parseFloat(e.target.value) || 0)}
                />
                <span className="input-group-text">TL</span>
              </div>
            </div>
            <div className="col-md-3 d-flex align-items-end pb-1">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="newFoodEnabled"
                  checked={formData.foodIsEnabled}
                  onChange={e => set("foodIsEnabled", e.target.checked)}
                  style={{ width: "2.5rem", height: "1.3rem" }}
                />
                <label className="form-check-label ms-1 fw-semibold" htmlFor="newFoodEnabled">
                  {formData.foodIsEnabled
                    ? <span className="text-success">Aktif</span>
                    : <span className="text-danger">Pasif</span>}
                </label>
              </div>
            </div>
            <div className="col-md-12">
              <label className="form-label fw-semibold small mb-1">Fotoğraf (opsiyonel)</label>
              <input
                className="form-control"
                type="file"
                accept="image/*"
                onChange={e => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                  setPreview(f ? URL.createObjectURL(f) : null);
                }}
              />
              {preview && (
                <div className="mt-2 text-center">
                  <img
                    src={preview}
                    alt="önizleme"
                    style={{ height: "120px", objectFit: "cover", borderRadius: "10px", border: "2px solid #198754" }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── EKSTRALAR ── */}
        {activeTab === "ekstra" && (
          <div>
            <div className="p-3 rounded mb-3" style={{ background: "#f8f9fa", border: "1px solid #e0e0e0" }}>
              <div className="fw-semibold mb-2 small text-muted">
                <i className="bi bi-plus-lg me-1"></i>Yeni Ekstra Ekle
              </div>
              <div className="row g-2">
                <div className="col-md-5">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Başlık (ör. Ketçap)"
                    value={menuExtra.menuUpgradeTitle}
                    onChange={e => setMenuExtra(p => ({ ...p, menuUpgradeTitle: e.target.value }))}
                  />
                </div>
                <div className="col-md-3">
                  <div className="input-group input-group-sm">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Fiyat"
                      value={menuExtra.menuUpgradePrice}
                      onChange={e => setMenuExtra(p => ({ ...p, menuUpgradePrice: parseFloat(e.target.value) || 0 }))}
                    />
                    <span className="input-group-text">TL</span>
                  </div>
                </div>
                <div className="col-md-2">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Max adet"
                    value={menuExtra.menuUpgradeMaxQuantity}
                    onChange={e => setMenuExtra(p => ({ ...p, menuUpgradeMaxQuantity: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="col-md-1 d-flex align-items-center">
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input" type="checkbox"
                      id="newExtraEnabled"
                      checked={menuExtra.menuUpgradeIsEnabled}
                      onChange={e => setMenuExtra(p => ({ ...p, menuUpgradeIsEnabled: e.target.checked }))}
                    />
                    <label className="form-check-label small" htmlFor="newExtraEnabled">Aktif</label>
                  </div>
                </div>
                <div className="col-md-1">
                  <button
                    className="btn btn-success btn-sm w-100"
                    onClick={addExtra}
                    disabled={!menuExtra.menuUpgradeTitle.trim()}
                  >
                    <i className="bi bi-plus-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            {formData.menuUpgrade.length === 0 ? (
              <p className="text-muted text-center small py-3">Henüz ekstra eklenmemiş.</p>
            ) : (
              <div className="d-flex flex-column gap-2">
                {formData.menuUpgrade.map((ex, i) => (
                  <div
                    key={i}
                    className="d-flex align-items-center justify-content-between px-3 py-2 rounded"
                    style={{ background: "#fff", border: "1px solid #e0e0e0" }}
                  >
                    <div>
                      <span className="fw-semibold">{ex.menuUpgradeTitle}</span>
                      <span className="ms-2 text-muted small">
                        {ex.menuUpgradePrice === 0 ? "Ücretsiz" : `${ex.menuUpgradePrice} TL`}
                        {" · "}Max {ex.menuUpgradeMaxQuantity} adet
                      </span>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      style={{ borderRadius: "50%", width: "28px", height: "28px", padding: 0, lineHeight: "26px" }}
                      onClick={() => removeExtra(i)}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ background: "#f0fff4", borderTop: "1px solid #c3e6cb" }}>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
          <i className="bi bi-x-lg me-1"></i>İptal
        </Button>
        <Button variant="success" onClick={handleSave} disabled={saving}>
          {saving && countdown === 0 ? (
            <><span className="spinner-border spinner-border-sm me-2" role="status" /><span>Kaydediliyor...</span></>
          ) : (
            <><i className="bi bi-floppy me-1"></i>Kaydet</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddNewFoodPopup;